/* eslint-disable camelcase */
/* eslint-disable max-len */
import React, { useContext } from 'react';
import { FormattedMessage, useTranslator } from '@jutro/locale';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';

import { MLProductCodeUtil } from 'ml-portals-util-js';
import _ from 'lodash';

import messages from './MLSelectPolicyToPayComponent.messages.js';
import metadata from './MLSelectPolicyToPayComponent.metadata.json5';
import styles from './MLSelectPolicyToPayComponent.module.scss';

import { IntlContext } from '@jutro/legacy/locale';

// MLSelectPolicyToPayDirectiveCtrl.js, ml-select-policy-to-pay.html
function MLSelectPolicyToPayComponent(props) {
    const translator = useTranslator();
    const intl = useContext(IntlContext);

    const {
        policySummaries,
        policiesBillingData,
        bundledPaymentRequest,
        isGpa,
        setSelectedPolicy,
        unsetSelectedPolicy,
        checkSplPreferredPaymentWarning
    } = props;

    const getPolicyDetails = (policy) => {
        if (policySummaries && !policy.policyDetails) {
            const foundPolicy = policySummaries.find((policyDetail) => policyDetail.policyNumber === policy.policyNumber_ML);
            policy.policyDetails = foundPolicy;
        }
        return policy.policyDetails;
    };

    const isAutoPolicy = (productCode) => MLProductCodeUtil.isPersonalAuto(productCode);
    const isHomePolicy = (productCode) => MLProductCodeUtil.isHomeOwners(productCode);
    const isUmbrellaPolicy = (productCode) => MLProductCodeUtil.isPersonalUmbrella(productCode);

    // const lobIconPath = LineOfBusinessUtil.lobIconPath;

    const getPolicyStatus = (policy) => {
        if (policy.policyDetails.isCancelled) {
            return translator(messages.cancelled);
        }
        if (policy.policyDetails.isRenewed_ML && policy.termNumber_ML === policy.policyDetails.termNumber_ML) {
            return translator(messages.renewed);
        }
        return translator(messages.active);
    };

    const calculatePolicyBalance = (policy, balance) => {
        return policy.reinstatementAmountDue_ML && (policy.reinstatementAmountDue_ML.amount > balance.amount) ? policy.reinstatementAmountDue_ML : balance;
    };

    const calculateEarlierTermsRemainingBalance = (policy) => {
        if (!policy.previousTermsRemainingAmounts) {
            return 0;
        }

        return policy.previousTermsRemainingAmounts;
    };

    const calculateTotalPolicyBalance = (policy) => {
        const policies = policiesBillingData.filter((d) => d.policyID_ML === policy.policyID_ML);
        const sum = policies.reduce((prev, cur) => {
            return prev + cur.remainingPolicyBalance_ML.amount + calculateEarlierTermsRemainingBalance(policy);
        }, 0);
        const balance = { 'amount': _.round(sum, 2), 'currency': policy.remainingPolicyBalance_ML.currency };
        return calculatePolicyBalance(policy, balance);
    };

    const isPolicyEligibleForPayment = (policy) => {
        const _hasPositiveValue = (currencyAmount) => {
            return currencyAmount && currencyAmount.amount && currencyAmount.amount > 0;
        };
        return _hasPositiveValue(calculateTotalPolicyBalance(policy));
    };

    const selectPolicy = (policy, selected) => {
        if (policy.selected) {
            const paymentRequestIndex = bundledPaymentRequest.paymentRequests.value.findIndex((paymentRequest) => paymentRequest.policyNumber === policy.policyNumber_ML);
            bundledPaymentRequest.paymentRequests.value.splice(paymentRequestIndex, 1);
            unsetSelectedPolicy(policy);
        } else {
            const newPaymentRequest = {
                policyNumber: policy.policyNumber_ML,
                termNumber: policy.termNumber_ML,
                policyBalance: calculateTotalPolicyBalance(policy).amount,
                paymentType: 'OTP',
                productCode: policy.policyDetails.product.publicID,
                invoiceNumber_ML: (_.isArray(policy.unpaidInvoices) && policy.unpaidInvoices.length > 0) ? policy.unpaidInvoices[0].number : '',
                isModernProduct_Ext: policy.policyDetails?.isModernProduct_Ext,
                preferredPaymentMethodInd_Ext: policy.policyDetails?.preferredPaymentMethodInd_Ext
            };
            bundledPaymentRequest.paymentRequests.value.push(newPaymentRequest);
            setSelectedPolicy(policy);
        }

        policy.selected = selected;
        checkSplPreferredPaymentWarning();
    };

    const generateOverrides = () => {
        const getIcon = (policy) => {
            if (isAutoPolicy(policy.policyDetails.product.publicID)) {
                return "icon-ml-car-icon-outline";
            }
            if (isHomePolicy(policy.policyDetails.product.publicID)) {
                return "icon-ml-home-detailed-icon";
            }
            if (isUmbrellaPolicy(policy.policyDetails.product.publicID)) {
                return "icon-ml-umbrella-icon";
            }
            return '';
        };

        const getProduct = (policy) => {
            if (isAutoPolicy(policy.policyDetails.product.publicID)) {
                return translator(messages.autoPolicy);
            }
            if (isHomePolicy(policy.policyDetails.product.publicID)) {
                return translator(messages.homePolicy);
            }
            if (isUmbrellaPolicy(policy.policyDetails.product.publicID)) {
                return translator(messages.umbrellaPolicy);
            }
            return '';
        };

        const getPolicyTerm = (termType) => {
            if (termType === 'Annual') {
                return translator(messages.year);
            }
            if (termType === 'HalfYear') {
                return translator(messages.sixMonths);
            }
        };

        const formatAmountString = (amount) => {
            const premium = _.get(amount, 'amount', 0);
            const currency = _.get(amount, 'currency', 'usd');

            try {
                return intl.formatNumber(premium, { style: 'currency', currency });
            } catch (e) {
                return '';
            }
        };

        const formatDateString = (date) => {
            return intl.formatDate(new Date(date.year, date.month, date.day), { year: 'numeric', month: '2-digit', day: '2-digit' });
        };

        const getPolicyDisplayName = ({ policyDetails }) => {
            if (isHomePolicy(policyDetails.product.publicID)) {
                return policyDetails.address_ML.displayName;
            }
            if (isAutoPolicy(policyDetails.product.publicID)) {
                if (policyDetails.numberOfVehicles_ML === 0) {
                    return translator(messages.namedNonOwner);
                }
                if (policyDetails.numberOfVehicles_ML === 1) {
                    return translator(messages.Vehicle);
                }
                if (policyDetails.numberOfVehicles_ML > 1) {
                    return translator(messages.numberOfVehicles, { vehiclesNumber: policyDetails.numberOfVehicles_ML });
                }
            }
            if (isUmbrellaPolicy(policyDetails.product.publicID)) {
                return translator(messages.coverageLimitExcessLiability, { coverageLimit: formatAmountString(policyDetails.excessLimit_ML) });
            }
            return '';
        };

        const getAllTermsOutstandingAmount = (policy) => {
            const amount = policy.totalOutstandingAmount_ML.amount + (policy.previousTermsOutstandingAmounts ?? 0);
            return { currency: policy.totalOutstandingAmount_ML.currency, amount: amount };
        };

        const overrides = policiesBillingData.map((policy, index) => {
            return {
                [`policy${index}`]: {
                    visible: getPolicyDetails(policy),
                    className: styles.policyRow
                },
                [`checkbox${index}`]: {
                    onValueChange: (value) => selectPolicy(policy, value),
                    value: policy.selected,
                    disabled: !isPolicyEligibleForPayment(policy)
                },
                [`noGutterContainer${index}`]: {
                    className: isGpa ? 'no-gutter col-sm-4 divider' : 'no-gutter col-sm-6'
                },
                [`icon${index}`]: {
                    className: getIcon(policy)
                },
                [`product${index}`]: {
                    content: getProduct(policy)
                },
                [`policyNumber${index}`]: {
                    content: translator(messages.policyNumber, { policyNumber: policy.policyNumber_ML })
                },
                [`premiumSection${index}`]: {
                    visible: !isGpa
                },
                [`termType${index}`]: {
                    content: (
                        <FormattedMessage
                            {...messages.premiumPerTerm}
                            values={{
                                premium: (<strong>{formatAmountString(policy.policyDetails.totalCost_ML)}</strong>),
                                term: getPolicyTerm(policy.policyDetails.termType_ML)
                            }}
                        />
                    )
                },
                [`policyDetail${index}`]: {
                    content: getPolicyDisplayName(policy)
                },
                [`stateFees${index}`]: {
                    visible: policy.policyDetails.totalChanges_Ext.amount !== 0,
                    totalFees: _.get(policy.policyDetails, 'totalChanges_Ext', {}),
                    itemizedFees: _.get(policy.policyDetails, 'feesCosts_Ext', []),
                    displayAlternativeTemplate: false
                },
                [`totalAmount${index}`]: {
                    content: translator(messages.amountDue, { amountDue: formatAmountString(policy.totalOutstandingAmount_ML) })
                },
                [`gpaContainer${index}`]: {
                    visible: isGpa
                },
                [`gpaTermType${index}`]: {
                    content: (
                        <FormattedMessage
                            {...messages.premiumPerTerm}
                            values={{
                                premium: (<strong>{formatAmountString(policy.policyDetails.totalCost_ML)}</strong>),
                                term: getPolicyTerm(policy.policyDetails.termType_ML)
                            }}
                        />
                    )
                },
                [`gpaPolicyDisplayName${index}`]: {
                    content: getPolicyDisplayName(policy)
                },
                [`gpaTotalAmount${index}`]: {
                    content: translator(messages.amountDue, { amountDue: formatAmountString(getAllTermsOutstandingAmount(policy)) })
                },
                [`policyStatusSection${index}`]: {
                    className: isGpa ? 'col-sm-4' : 'col-sm-6',
                    content: (
                        <p className="body-copy">
                            <span className="font-weight-bold">{translator(messages.statusName, { status: getPolicyStatus(policy) })}</span>
                            <br />
                            {translator(messages.effectiveDate, { effectiveDate: formatDateString(policy.effectiveDate_ML) })}
                            <br />
                            {translator(messages.expirationDate, { expirationDate: formatDateString(policy.expirationDate_ML) })}
                        </p>
                    )
                },
                [`gpaStateFees${index}`]: {
                    visible: policy.policyDetails.totalChanges_Ext.amount !== 0,
                    totalFees: _.get(policy.policyDetails, 'totalChanges_Ext', {}),
                    itemizedFees: _.get(policy.policyDetails, 'feesCosts_Ext', [])
                }
            };
        });

        return Object.assign({}, ...overrides);
    };

    const overrides = {
        policyIterable: {
            data: policiesBillingData,
            className: styles.iterableTableStyle
        },
        ...generateOverrides()
    };

    const resolvers = {
        resolveClassNameMap: styles
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            overrideProps={overrides}
            classNameMap={resolvers.resolveClassNameMap}
        />
    );
}

export default MLSelectPolicyToPayComponent;
